import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  type?: "website" | "article" | "hotel";
  image?: string;
  structuredData?: object;
}

/**
 * SEO Component - Updates document head with page-specific meta tags
 * Improves search engine visibility and social sharing
 */
const SEO = ({
  title,
  description,
  canonical,
  type = "website",
  image = "https://lovable.dev/opengraph-image-p98pqg.png",
  structuredData,
}: SEOProps) => {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Helper to update or create meta tags
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? "property" : "name";
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Basic SEO meta tags
    setMetaTag("description", description);
    setMetaTag("robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

    // Open Graph tags
    setMetaTag("og:title", title, true);
    setMetaTag("og:description", description, true);
    setMetaTag("og:type", type, true);
    setMetaTag("og:image", image, true);
    setMetaTag("og:site_name", "Harbor Heights Extended Stay", true);
    setMetaTag("og:locale", "en_US", true);

    // Twitter Card tags
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);
    setMetaTag("twitter:image", image);

    // Canonical URL
    if (canonical) {
      setMetaTag("og:url", canonical, true);
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute("href", canonical);
    }

    // Local business SEO
    setMetaTag("geo.region", "US-IA");
    setMetaTag("geo.placename", "Sioux City");
    setMetaTag("geo.position", "42.4944;-96.3547");
    setMetaTag("ICBM", "42.4944, -96.3547");

    // Structured data
    if (structuredData) {
      let scriptTag = document.querySelector('script[data-seo="structured-data"]');
      if (!scriptTag) {
        scriptTag = document.createElement("script");
        scriptTag.setAttribute("type", "application/ld+json");
        scriptTag.setAttribute("data-seo", "structured-data");
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(structuredData);
    }

    // Cleanup function
    return () => {
      const structuredDataScript = document.querySelector('script[data-seo="structured-data"]');
      if (structuredDataScript) {
        structuredDataScript.remove();
      }
    };
  }, [title, description, canonical, type, image, structuredData]);

  return null;
};

export default SEO;

// Structured Data Templates for different page types
export const hotelStructuredData = {
  "@context": "https://schema.org",
  "@type": "Hotel",
  "name": "Harbor Heights Extended Stay",
  "description": "Affordable extended stay hotel with spacious suites and full kitchens in Sioux City, Iowa. Perfect for business travelers, families, and long-term guests.",
  "url": "https://www.extendedstayharborheights.com",
  "telephone": "+1-712-224-7003",
  "email": "stay@harborheightssiouxcity.com",
  "image": "https://www.extendedstayharborheights.com/og-image.png",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "6120 Harbor Drive",
    "addressLocality": "Sioux City",
    "addressRegion": "Iowa",
    "postalCode": "51111",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 42.4944,
    "longitude": -96.3547
  },
  "priceRange": "$",
  "starRating": {
    "@type": "Rating",
    "ratingValue": "4.8"
  },
  "checkinTime": "15:00",
  "checkoutTime": "11:00",
  "amenityFeature": [
    { "@type": "LocationFeatureSpecification", "name": "Full Kitchen", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Free Wi-Fi", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Fitness Center", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Free Parking", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Pet Friendly", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Weekly Housekeeping", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "24/7 Front Desk", "value": true }
  ],
  "numberOfRooms": 50,
  "petsAllowed": true,
  "paymentAccepted": "Cash, Credit Card, Debit Card",
  "currenciesAccepted": "USD"
};

export const localBusinessStructuredData = {
  "@context": "https://schema.org",
  "@type": "LodgingBusiness",
  "name": "Harbor Heights Extended Stay",
  "description": "Extended stay hotel in Sioux City, Iowa offering affordable weekly and monthly rates with full kitchenettes.",
  "url": "https://www.extendedstayharborheights.com",
  "telephone": "+1-712-224-7003",
  "email": "stay@harborheightssiouxcity.com",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "6120 Harbor Drive",
    "addressLocality": "Sioux City",
    "addressRegion": "Iowa",
    "postalCode": "51111",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 42.4944,
    "longitude": -96.3547
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    "opens": "00:00",
    "closes": "23:59"
  },
  "priceRange": "$50-$225",
  "areaServed": {
    "@type": "City",
    "name": "Sioux City",
    "containedInPlace": {
      "@type": "State",
      "name": "Iowa"
    }
  }
};

export const faqStructuredData = (faqs: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const breadcrumbStructuredData = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});
